
export const isGradient = (str: string): boolean => {
  if (!str || typeof str !== 'string') return false;
  return str.includes('linear-gradient') || str.includes('radial-gradient');
};

const cleanUrl = (url: string): string => {
  if (!url || typeof url !== 'string') return '';
  
  // Remove multiple protocol instances and trim
  const cleaned = url.replace(/^(https?:\/\/)+(.*)/i, '$1$2').trim();
  
  // Add https only if no protocol exists
  if (!/^https?:\/\//i.test(cleaned)) {
    return 'https://' + cleaned;
  }
  
  return cleaned;
};

const isValidUrl = (str: string): boolean => {
  try {
    if (!str || typeof str !== 'string') return false;
    if (isGradient(str)) return true;
    new URL(str);
    return true;
  } catch {
    return false;
  }
};

export const safeEncodeURI = (uri: string): string => {
  try {
    if (!uri) return '';
    
    // Don't encode gradients
    if (isGradient(uri)) {
      return uri;
    }

    // Clean the URL first
    const cleanedUrl = cleanUrl(uri);

    // Only encode if it's a valid URL
    if (isValidUrl(cleanedUrl)) {
      try {
        const urlObj = new URL(cleanedUrl);
        
        // IMPORTANT FIX: Only encode the pathname, and handle potential decoding errors
        try {
          // First try to decode to avoid double-encoding
          const decodedPath = decodeURIComponent(urlObj.pathname);
          urlObj.pathname = encodeURIComponent(decodedPath).replace(/%2F/g, '/');
        } catch (decodeError) {
          // If decoding fails, just encode directly
          urlObj.pathname = encodeURIComponent(urlObj.pathname).replace(/%2F/g, '/');
        }
        
        return urlObj.toString();
      } catch {
        // If URL manipulation fails, fallback to basic cleaning
        return cleanedUrl;
      }
    }

    console.warn('Invalid URL detected:', uri);
    return uri;
  } catch (e) {
    console.error('Error encoding URI:', e);
    return '';
  }
};

export const sanitizeProfileUrls = (profile: any): any => {
  if (!profile) return profile;

  // Create a new object to avoid mutating the original
  const sanitizedProfile = { ...profile };

  // Sanitize profile image URLs
  if (sanitizedProfile.profile_image_url) {
    sanitizedProfile.profile_image_url = safeEncodeURI(sanitizedProfile.profile_image_url);
  }
  if (sanitizedProfile.banner_image_url) {
    sanitizedProfile.banner_image_url = safeEncodeURI(sanitizedProfile.banner_image_url);
  }

  // Sanitize custom links
  if (Array.isArray(sanitizedProfile.custom_links)) {
    sanitizedProfile.custom_links = sanitizedProfile.custom_links.map((link: any) => ({
      ...link,
      url: link.url ? safeEncodeURI(link.url) : '',
      thumbnail: link.thumbnail ? (isGradient(link.thumbnail) ? link.thumbnail : safeEncodeURI(link.thumbnail)) : ''
    }));
  }

  return sanitizedProfile;
};
